import { trackPageView } from '@snowplow/browser-tracker'
import Cookies from 'js-cookie'
import { ReactNode, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import {
  ABTestConfig,
  ABTestProvider,
  useABTest,
} from '../contexts/ABTestContext'
import {
  addFacebookParamsToLocalStorage,
  FACEBOOK_CLICK_ID_KEY,
  getConfidenceVisitorId,
} from '../helpers/localStorage'
import { applyTrackerUserId } from '../helpers/trackerUserId'
import LoadingPage from '../pages/LoadingPage'

interface ABTestRootOutletProps {
  abTestConfig?: ABTestConfig
  children?: ReactNode
}

function RootOutletContent() {
  const [isReady, setIsReady] = useState<boolean>(false)
  const location = useLocation()

  useEffect(() => {
    // The auth listener may have reported the account before the tracker
    // existed, in which case that call went nowhere. A page view can only run
    // once the tracker is up, so this is the first safe moment to apply it.
    applyTrackerUserId()

    trackPageView({
      context: [
        {
          schema: 'iglu:com.asanarebel/marketing_ids/jsonschema/1-0-1',
          data: {
            fbc:
              Cookies.get('_fbc') ??
              localStorage.getItem(FACEBOOK_CLICK_ID_KEY),
            fbp: Cookies.get('_fbp'),
          },
        },
        {
          schema: 'iglu:com.asanarebel/confidence_visitor_id/jsonschema/1-0-0',
          data: {
            id: getConfidenceVisitorId(),
          },
        },
      ],
    })
  }, [location])

  useEffect(() => {
    addFacebookParamsToLocalStorage(location.search)

    // NOTE: Initially wait one second to let the Facebook Pixel do its job and
    // add Facebook params to local storage.
    const setReadyWithTimeout = setTimeout(() => {
      setIsReady(true)
    }, 1000)

    return () => clearTimeout(setReadyWithTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>{isReady ? <Outlet /> : <LoadingPage />}</>
}

/**
 * Wrapper that waits for the A/B test to finish loading before rendering
 * content, so the flag is resolved — and its "flag applied" record created —
 * before any page view fires.
 *
 * It used to poll for the visitor id cookie here as well, for up to two
 * seconds, because a page view firing before the SDK wrote that cookie
 * recorded no id. getConfidenceVisitorId now resolves an id synchronously on
 * first call, so there is nothing to poll for and nothing to delay the funnel
 * by.
 */
function ABTestContentWrapper() {
  const { isLoading } = useABTest()

  // Wait for A/B test to fully load before rendering RootOutletContent
  if (isLoading) {
    return <LoadingPage />
  }

  return <RootOutletContent />
}

/**
 * RootOutlet with optional A/B testing support
 * If abTestConfig is provided, wraps the outlet with ABTestProvider
 * Otherwise, behaves like the regular RootOutlet
 */
export default function ABTestRootOutlet({
  abTestConfig,
}: ABTestRootOutletProps) {
  if (abTestConfig) {
    return (
      <ABTestProvider config={abTestConfig}>
        <ABTestContentWrapper />
      </ABTestProvider>
    )
  }

  return <RootOutletContent />
}
